// Pan-back paging for lightweight-charts.
//
// Every price chart in the app loads a fixed window and then stops, so scrolling
// left runs into a wall. This attaches the missing behaviour to any chart
// instance: when the viewport approaches the oldest loaded bar, ask the owner for
// an older page.
//
// Two things make this fiddly, and both are handled here rather than in each
// caller:
//
//   1. Prepending bars shifts every logical index, so the viewport jumps
//      backwards unless it is restored. Restoring by *time* rather than by index
//      is stable across prepends — see restoreVisibleRange.
//   2. subscribeVisibleLogicalRangeChange fires continuously while dragging, so
//      the request has to be de-duplicated or one pan queues dozens of fetches.
//
// Forward paging is deliberately absent: charts keep everything from the loaded
// window through to now, and new bars arrive over SignalR, so panning right never
// runs out of data.

/** How close to the oldest loaded bar (in bars) triggers the next page. */
const DEFAULT_THRESHOLD = 12;

export interface OlderBarLoader {
  /**
   * Fetch and apply the next older page. Resolve with how many bars were added.
   *
   *   > 0  bars were prepended
   *     0  there is no more history — the loader latches off for this series
   *   < 0  not ready yet (no anchor bar loaded); stay armed and ask again
   *
   * The negative case matters: the first re-check can land before the series has
   * any data, and treating that as "no history" would silently disable paging
   * for the rest of the series' life.
   */
  onLoadOlder: () => Promise<number>;
  threshold?: number;
}

export interface PagingHandle {
  detach: () => void;
  /** Clear the exhausted flag — call when the symbol or timeframe changes. */
  reset: () => void;
  /**
   * Re-evaluate the current range. Call after applying data: subscribing to
   * range changes is not enough on its own, because a series that fits entirely
   * inside the viewport never produces another range event — the chart is
   * already showing everything it has, which is exactly when more history is
   * needed most.
   */
  recheck: () => void;
}

/**
 * Wire pan-back paging onto a chart. Returns a handle; call detach() when the
 * chart is disposed.
 */
export function attachOlderBarLoader(chart: any, opts: OlderBarLoader): PagingHandle {
  const threshold = opts.threshold ?? DEFAULT_THRESHOLD;
  let loading = false;
  let exhausted = false;
  let detached = false;

  const onRangeChange = (range: { from: number; to: number } | null) => {
    if (!range || loading || exhausted || detached) return;
    // `from` goes negative once the viewport scrolls past the first bar; either
    // way, small means "near the old edge".
    if (range.from > threshold) return;

    console.debug('[Paging] requesting older bars, range.from =', range.from);
    loading = true;
    void (async () => {
      try {
        const added = await opts.onLoadOlder();
        if (added === 0) exhausted = true;   // negative = not ready, stay armed
      } catch {
        // Leave `exhausted` alone: a transient failure should not permanently
        // stop paging, and `loading` clearing below lets the next pan retry.
      } finally {
        loading = false;
      }
    })();
  };

  const timeScale = chart?.timeScale?.();
  console.debug('[Paging] attached to chart, timeScale =', !!timeScale);
  timeScale?.subscribeVisibleLogicalRangeChange?.(onRangeChange);

  // Evaluate the range we're attaching to, don't just wait for the next change.
  // The initial fit fires its range event before this listener exists, and if the
  // loaded data doesn't fill the viewport nothing will ever move it again — so
  // without this a short series simply never pages.
  try { onRangeChange(timeScale?.getVisibleLogicalRange?.() ?? null); } catch { /* no data yet */ }

  return {
    detach: () => {
      detached = true;
      try { timeScale?.unsubscribeVisibleLogicalRangeChange?.(onRangeChange); } catch { /* disposed */ }
    },
    reset: () => { exhausted = false; loading = false; },
    recheck: () => {
      // Deferred a frame: immediately after setData/fitContent the time scale has
      // not laid out yet and getVisibleLogicalRange still reads null or stale.
      requestAnimationFrame(() => {
        if (detached) return;
        try { onRangeChange(timeScale?.getVisibleLogicalRange?.() ?? null); } catch { /* no data */ }
      });
    },
  };
}

/**
 * Capture the visible span as *times*, for restoring after a prepend.
 * Returns null when the chart has no established range yet (first paint), in
 * which case the caller should leave the default auto-fit alone.
 */
export function captureVisibleRange(chart: any): { from: number; to: number } | null {
  try {
    const r = chart?.timeScale?.()?.getVisibleRange?.();
    if (!r || r.from == null || r.to == null) return null;
    return { from: Number(r.from), to: Number(r.to) };
  } catch {
    return null;
  }
}

/**
 * Put the viewport back where it was before older bars were prepended. Must run
 * after the new data has been applied to the series, or lightweight-charts
 * clamps the range to the data it currently holds.
 */
export function restoreVisibleRange(chart: any, range: { from: number; to: number } | null) {
  if (!range) return;
  try {
    chart?.timeScale?.()?.setVisibleRange?.({ from: range.from as any, to: range.to as any });
  } catch {
    // Range outside the loaded data (symbol switched mid-flight) — the chart's
    // own auto-fit is a fine fallback.
  }
}
